import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useSeo } from '../hooks/useSeo.ts';
import { trackEvent } from '../analytics.ts';

/**
 * Deutsche Variante der EAA-Compliance-Landing-Page für die DACH-Anzeigenkampagne
 * (EAAProof_Search_Compliance-DE_2026). Sitzt unter /de/lp/eaa-compliance.
 *
 * Conversion-Lift vs. englischer Seite: in der Regel +30–60 % bei deutschsprachigem
 * Suchtraffic. Pflicht für DE/AT-Märkte wo die englische LP wie eine fremde Marke wirkt.
 *
 * TODO: NATIVE-SPEAKER REVIEW BENÖTIGT — diese Übersetzung wurde vom Modell erstellt und
 * sollte vor dem Live-Schalten der DE-Kampagne von einem deutschen Muttersprachler geprüft
 * werden. Marketing-Sprache ("Knock-out-Schlagzeilen", regulatorische Begriffe) hat
 * jurisdiktionelle Feinheiten, die nur ein Native bemerkt.
 */
export default function LandingComplianceLpDe() {
  useSeo({
    title: 'Barrierefreiheits-Audit für das BFSG — €29',
    canonicalPath: '/de/lp/eaa-compliance',
    noindex: true,
    description:
      'Das BFSG ist seit 28. Juni 2025 in Kraft. Vollständige WCAG-2.1-AA-Prüfung Ihrer Website + veröffentlichungsbereite Barrierefreiheitserklärung — ab €29. Kein Berater notwendig.',
    alternates: [
      { lang: 'en',        href: 'https://eaaproof.com/lp/eaa-compliance' },
      { lang: 'de',        href: 'https://eaaproof.com/de/lp/eaa-compliance' },
      { lang: 'fr',        href: 'https://eaaproof.com/fr/lp/eaa-compliance' },
      { lang: 'x-default', href: 'https://eaaproof.com/lp/eaa-compliance' },
    ],
  });

  useEffect(() => {
    trackEvent('lp_view', { lp: 'eaa-compliance-de' });
  }, []);

  return (
    <div className="page lp-page" lang="de">
      <section className="lp-hero">
        <span className="hero-badge">
          <span className="hero-badge-dot" aria-hidden="true" />
          Seit 28. Juni 2025 EU-weit verpflichtend
        </span>
        <h1>
          In 60 Sekunden: Ihr <span className="accent">BFSG-Barrierefreiheits-Audit</span>.
        </h1>
        <p className="lead">
          Das Barrierefreiheitsstärkungsgesetz (BFSG) — die deutsche Umsetzung der
          EU-Richtlinie 2019/882 — ist bereits in Kraft. Vollständiger WCAG-Audit Ihrer Website
          und eine veröffentlichungsbereite Barrierefreiheitserklärung —
          <strong> ab €29</strong>, ohne Berater.
        </p>

        <div className="lp-cta-row">
          <Link
            to="/pricing"
            className="cta"
            onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-de', placement: 'hero' })}
          >
            Barrierefreiheits-Audit starten
          </Link>
          <Link to="/sample" className="cta secondary">Beispielbericht ansehen</Link>
        </div>
        <p className="lp-trust-line">
          <span aria-hidden="true">✓</span> Einmalig €29
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> Stripe-sichere Bezahlung
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> 7 Tage Rückgaberecht
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> EU-USt. automatisch
        </p>
      </section>

      <section className="lp-fines">
        <h2>Was das BFSG abdeckt</h2>
        <p className="lp-section-lead">
          Das Barrierefreiheitsstärkungsgesetz (BFSG) — die deutsche Umsetzung der EU-Richtlinie
          2019/882 — gilt für digitale Produkte und Dienstleistungen, die EU-Verbraucherinnen und
          -Verbrauchern angeboten werden. Den Anwendungsbereich zu verstehen ist der erste Schritt
          jeder Compliance-Prüfung; wir empfehlen, den offiziellen Gesetzestext zu prüfen und
          qualifizierten Rechtsrat einzuholen.
        </p>
        <div className="lp-fines-grid">
          <article>
            <h3>Für wen es gilt</h3>
            <p>Unternehmen, die digitale Produkte oder Dienstleistungen an EU-Verbraucher verkaufen — darunter E-Commerce, Banking, E-Books, Ticketing und die meisten verbrauchernahen Web-Anwendungen.</p>
          </article>
          <article>
            <h3>Ausnahmen für Kleinstunternehmen</h3>
            <p>Unternehmen mit weniger als 10 Beschäftigten und unter €2 Mio. Jahresumsatz sind für Dienstleistungen ausgenommen (nicht für physische Produkte) — siehe Artikel 4 der Richtlinie.</p>
          </article>
          <article>
            <h3>Technische Norm</h3>
            <p>EN 301 549 ist die vom BFSG herangezogene harmonisierte Norm, die für Web-Inhalte derzeit auf WCAG 2.1 AA abbildet.</p>
          </article>
          <article>
            <h3>Durchsetzung</h3>
            <p>Die zuständige Marktüberwachungsbehörde setzt das Gesetz durch; Verfahren und mögliche Maßnahmen ergeben sich aus dem nationalen Recht.</p>
          </article>
        </div>
        <p className="lp-section-disclaimer">
          Diese Informationen dienen ausschließlich der allgemeinen Orientierung und stellen keine
          Rechtsberatung dar. Für verbindliche, auf Ihre Situation zugeschnittene Auskünfte wenden
          Sie sich bitte an eine qualifizierte Rechtsanwältin oder einen qualifizierten Rechtsanwalt
          in Ihrer Jurisdiktion.
        </p>
      </section>

      <section className="lp-how">
        <h2>Was Sie bekommen</h2>
        <ol className="lp-steps">
          <li>
            <strong>Schritt 1 — URL eingeben.</strong>
            <span> Unser Scanner lädt Ihre Seite in einem echten Chromium-Browser und führt axe-core 4.10 gegen die WCAG-2.1-AA-Kriterien aus.</span>
          </li>
          <li>
            <strong>Schritt 2 — Regulatorisch belastbares PDF erhalten.</strong>
            <span> Jeder Verstoß wird mit der genauen Klausel der EAA / EN 301 549 zitiert, inklusive verständlicher Behebungsanleitung von Claude Sonnet — verankert in Ihrem HTML, keine Halluzinationen.</span>
          </li>
          <li>
            <strong>Schritt 3 — Erklärung veröffentlichen.</strong>
            <span> Legen Sie die erzeugte Barrierefreiheitserklärung unter <code>/accessibility</code> ab. Sie erfüllt Artikel 7 der Web-Accessibility-Richtlinie nach der EU-Mustererklärung (Durchführungsbeschluss (EU) 2018/1523).</span>
          </li>
        </ol>
      </section>

      <section className="lp-pricing">
        <h2>Preise</h2>
        <div className="lp-pricing-grid">
          <div className="plan-card">
            <h3>Einzel-Scan</h3>
            <p className="price">€29<span> einmalig</span></p>
            <p className="muted">1 vollständiger Audit + Erklärung</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-de', placement: 'pricing_single' })}
            >Einzel-Scan kaufen</Link>
          </div>
          <div className="plan-card plan-card-featured">
            <p className="plan-badge">Am häufigsten gewählt</p>
            <h3>Basic</h3>
            <p className="price">€9<span>/Monat</span></p>
            <p className="muted">5 Scans pro Monat</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-de', placement: 'pricing_basic' })}
            >Basic starten</Link>
          </div>
          <div className="plan-card">
            <h3>Agency</h3>
            <p className="price">€49<span>/Monat</span></p>
            <p className="muted">50 Scans, mit Ihrem Branding</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-de', placement: 'pricing_agency' })}
            >Agency starten</Link>
          </div>
        </div>
      </section>

      <section className="lp-faq">
        <h2>Häufig gestellte Fragen</h2>
        <details>
          <summary>Reicht ein Einzel-Scan für die BFSG-Konformität?</summary>
          <p>
            Für eine kleine, sich selten ändernde Website ja — vorausgesetzt, Sie setzen die im
            Bericht aufgeführten Behebungsmaßnahmen um. Für Sites, die wöchentlich aktualisiert
            werden, empfehlen wir den <strong>Basic-Plan zu €9/Monat</strong>, damit Sie nach
            jedem Release erneut prüfen können.
          </p>
        </details>
        <details>
          <summary>WCAG 2.1 oder 2.2 — welche Version deckt der Bericht ab?</summary>
          <p>
            Die EAA verweist auf EN 301 549, die derzeit WCAG 2.1 AA vorschreibt. Unser Scanner
            führt den vollständigen WCAG-2.1-AA-Regelsatz über axe-core aus. Die kommende
            EN-301-549-Aktualisierung hebt die Basislinie auf 2.2 AA an; wir liefern das
            Upgrade automatisch aus — keine Planänderung erforderlich.
          </p>
        </details>
        <details>
          <summary>Was, wenn Claudes Behebungsvorschlag nicht zu meinem Code passt?</summary>
          <p>
            Fünf Verankerungs-Schutzschichten verhindern halluzinierte Fixes: Temperatur 0,
            Index-Echo pro Verstoß, strenge Teilstring-Verankerung bei jedem Codebeispiel und
            ein Strip-bei-Mismatch-Fallback. Wenn eine Schicht greift, erhalten Sie weiterhin
            den axe-core-Befund — nur ohne KI-Kommentar. Niemals einen erfundenen Fix.
          </p>
        </details>
        <details>
          <summary>Muss ich die Barrierefreiheitserklärung selbst hosten?</summary>
          <p>
            Sie erhalten eine einzelne HTML-Datei, die Sie in jeden statischen Host oder jedes
            CMS unter <code>/accessibility</code> einbinden können. Wir stellen den PDF-Bericht
            und die Erklärung außerdem über Ihr Konto für die Laufzeit Ihres Abonnements
            bereit.
          </p>
        </details>
        <details>
          <summary>Erstattungsrichtlinie?</summary>
          <p>
            7 Tage Geld-zurück-Garantie auf den Einzel-Scan-Plan und innerhalb der ersten 14
            Tage jeder Abonnementlaufzeit, ohne Begründungspflicht. Die EU-Fernabsatz-Regeln
            gelten zusätzlich.
          </p>
        </details>
      </section>

      <section className="lp-final-cta">
        <h2>Starten Sie Ihren Scan jetzt</h2>
        <p className="lead">€29. Eine URL. WCAG-Prüfbericht + veröffentlichungsbereite Erklärung in weniger als 2 Minuten in Ihrem Postfach.</p>
        <Link
          to="/pricing"
          className="cta"
          onClick={() => trackEvent('lp_cta_click', { lp: 'eaa-compliance-de', placement: 'final' })}
        >Mein BFSG-Barrierefreiheits-Audit starten</Link>
      </section>
    </div>
  );
}
